import React from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import '../styles/DatabasesPage.scss';

// A highly available managed datastore is not one machine: it is three of them behind a load
// balancer, and all three are billed. That is the whole reason this is a component and not three
// lines of JSX pasted into each create form - the failure mode we are guarding against is somebody
// ticking a box and tripling their bill without noticing, so the price is rendered next to the
// checkbox, always. The explanation (what HA does, the hourly figures) sits on hover: one line in
// the form, not a wall of text (Milos, 2026-10-25).
export const HA_MEMBERS = 3;

// Same convention as CreateInstancePage: a month is ~730 hours, and the rate comes from the
// selected size's REAL hourly_rate (pricing_rules, attached by /v1/flavors). Never hardcode a
// price table here: a stale number in the UI is a number the customer will argue about later.
const HOURS_PER_MONTH = 730;

// How many machines an HA datastore actually has, read from what the API returned rather than
// assumed. `member_ips` comes back as whatever the backend serialised (a JSON array, a
// comma-separated string, or nothing at all on an older row), so parse all three and return null
// when there is nothing to count - a list view that invents "3 machines" for a row the API never
// described is reporting an inference as a fact.
export const haMemberCount = (memberIps?: string | string[] | null): number | null => {
    if (Array.isArray(memberIps)) return memberIps.length || null;
    if (typeof memberIps !== 'string') return null;
    const raw = memberIps.trim();
    if (!raw) return null;
    if (raw.startsWith('[')) {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) && parsed.length ? parsed.length : null;
        } catch (_e) {
            return null;
        }
    }
    const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
    return parts.length || null;
};

interface HaOptionProps {
    enabled: boolean;
    onChange: (next: boolean) => void;
    // Hourly rate of the currently selected server size, or null while none is selected.
    hourlyRate: number | null;
    // What is being created, for the plain-language line ("PostgreSQL", "ClickHouse").
    serviceLabel: string;
    // When set, the toggle is disabled and this is shown as the reason (e.g. the no-card trial).
    disabledReason?: string | null;
}

const HaOption: React.FC<HaOptionProps> = ({ enabled, onChange, hourlyRate, serviceLabel, disabledReason }) => {
    const rate = hourlyRate != null && Number.isFinite(Number(hourlyRate)) ? Number(hourlyRate) : null;
    const haHour = rate != null ? rate * HA_MEMBERS : null;
    const haMonth = haHour != null ? haHour * HOURS_PER_MONTH : null;
    const extraMonth = rate != null && haMonth != null ? haMonth - rate * HOURS_PER_MONTH : null;
    const locked = !!disabledReason;

    return (
        <div className={`ha-option${enabled ? ' ha-option-on' : ''}${locked ? ' ha-option-locked' : ''}`}>
            <label className="ha-option-header">
                <input
                    type="checkbox"
                    checked={enabled}
                    disabled={locked}
                    onChange={e => onChange(e.target.checked)}
                    aria-describedby="ha-option-tip"
                />
                <span className="ha-option-title">High availability</span>
                <span className="ha-option-price">
                    {haMonth != null
                        ? <><strong>£{haMonth.toFixed(2)} / month</strong> · {HA_MEMBERS} machines</>
                        : <>{HA_MEMBERS} machines, about {HA_MEMBERS}x the price</>}
                </span>
                <FaInfoCircle className="ha-option-info" aria-hidden="true" />
            </label>

            {/* Shown on hover (and keyboard focus) only: the row above is the whole control. */}
            <div className="ha-tip" id="ha-option-tip" role="tooltip">
                <p>
                    {HA_MEMBERS} machines with automatic failover: if the one serving your {serviceLabel} stops, another
                    takes over on its own, and your app keeps the same address.
                </p>
                <p>
                    {haHour != null && rate != null && extraMonth != null
                        ? <>£{haHour.toFixed(5)} / hour for {HA_MEMBERS} machines (one machine: £{rate.toFixed(5)} / hour), about £{extraMonth.toFixed(2)} / month more. Billed hourly, at ~{HOURS_PER_MONTH} hours a month.</>
                        : <>Pick a server size to see the price: it is about {HA_MEMBERS}x the price of that size.</>}
                </p>
            </div>

            {locked && <p className="ha-locked-note">{disabledReason}</p>}
        </div>
    );
};

export default HaOption;
